// src/components/shared/RoleInvitationsPanel.js
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { roleInvitationService } from '../../services/roleInvitationService';
import { Shield, Check, X, Loader2, BookOpen, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export const RoleInvitationsPanel = () => {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      loadInvitations();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadInvitations = async () => {
    try {
      setError(null);
      console.log('Loading invitations for:', user.uid, user.email);
      const data = await roleInvitationService.getUserPendingInvitations(user.uid, user.email);
      console.log('Invitations loaded:', data);
      setInvitations(data);
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId) => {
    setProcessing(invitationId);
    try {
      await roleInvitationService.acceptInvitation(invitationId);
      setInvitations(prev => prev.filter(i => i.id !== invitationId));
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to accept invitation');
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (invitationId) => {
    setProcessing(invitationId);
    try {
      await roleInvitationService.declineInvitation(invitationId);
      setInvitations(prev => prev.filter(i => i.id !== invitationId));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  // Show error if query failed (likely missing index)
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">
        Failed to load invitations: {error}
      </div>
    );
  }

  if (invitations.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-4">
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm">
        <Shield className="w-4 h-4 text-emerald-600" />
        Pending Role Invitations ({invitations.length})
      </h3>
      
      <div className="space-y-2">
        {invitations.map(inv => (
          <div key={inv.id} className="bg-white rounded-lg p-3 shadow-sm border">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{inv.bookName}</p>
                  <p className="text-xs text-gray-600">
                    <span className="capitalize font-medium text-emerald-700">{inv.role}</span>
                    {' · '}
                    <span className="text-gray-500">
                      {inv.inviterName} · {formatDistanceToNow(
                        inv.createdAt?.toDate?.() || new Date(inv.createdAt), 
                        { addSuffix: true }
                      )}
                    </span>
                  </p>
                </div>
              </div>
              
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => handleDecline(inv.id)}
                  disabled={processing === inv.id}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  title="Decline"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleAccept(inv.id)}
                  disabled={processing === inv.id}
                  className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 flex items-center gap-1"
                >
                  {processing === inv.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Accept
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};